import axios from 'axios';
import { API_CONFIG } from '../config/api';
import mockApiService from './mock-api';

// Check if we should use mock data (frontend-only mode)
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK === 'true' || 
                      !process.env.REACT_APP_API_URL || 
                      process.env.REACT_APP_API_URL === 'http://localhost:9211';

class ApiService {
  cache = new Map();
  
  constructor() {
    // If using mock data, skip axios setup
    if (USE_MOCK_DATA) {
      console.log('🔧 Using Mock API Service (Frontend-only mode)');
      this.api = null; // No axios instance needed
      return;
    }
    
    this.api = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(config => {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }, error => {
      return Promise.reject(error);
    });

    // Response interceptor to handle token refresh
    this.api.interceptors.response.use(response => response, async error => {
      const originalRequest = error.config;
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const refreshToken = localStorage.getItem('refresh_token');
          if (refreshToken) {
            const response = await this.refreshToken(refreshToken);
            const { access_token } = response.data;
            localStorage.setItem('access_token', access_token);
            originalRequest.headers.Authorization = `Bearer ${access_token}`;
            return this.api(originalRequest);
          }
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    });
  }

  buildKey(url, body) {
    return `${url}|${body ? JSON.stringify(body) : ''}`;
  }

  async postCached(url, body, ttlMs) {
    if (USE_MOCK_DATA) {
      return await mockApiService.postCached(url, body, ttlMs);
    }
    
    const key = this.buildKey(url, body);
    const now = Date.now();
    const cached = this.cache.get(key);
    
    if (cached && cached.expiresAt > now) {
      return cached.response;
    }
    
    const res = await this.api.post(url, body);
    this.cache.set(key, {
      expiresAt: now + ttlMs,
      response: res
    });
    return res;
  }

  // Basic post method
  async post(url, body) {
    if (USE_MOCK_DATA) {
      return await mockApiService.post(url, body);
    }
    return await this.api.post(url, body);
  }

  // Basic get method
  async get(url, params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.get(url, params);
    }
    return await this.api.get(url, params);
  }

  // Authentication
  async login(credentials) {
    if (USE_MOCK_DATA) {
      const response = await mockApiService.login(credentials);
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      return response;
    }
    
    const formData = new FormData();
    formData.append('grant_type', 'password');
    formData.append('username', credentials.username);
    formData.append('password', credentials.password);
    formData.append('scope', '');
    formData.append('client_id', '');
    formData.append('client_secret', '');
    
    const response = await this.api.post(API_CONFIG.ENDPOINTS.LOGIN, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    return response;
  }

  async refreshToken(refreshToken) {
    if (USE_MOCK_DATA) {
      return await mockApiService.refreshToken(refreshToken);
    }
    
    const formData = new FormData();
    formData.append('grant_type', 'refresh_token');
    formData.append('refresh_token', refreshToken);
    formData.append('scope', '');
    formData.append('client_id', '');
    formData.append('client_secret', '');
    
    return await this.api.post(API_CONFIG.ENDPOINTS.REFRESH_TOKEN, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  }

  async logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  }

  // User Profile
  async getUserData() {
    if (USE_MOCK_DATA) {
      return await mockApiService.getUserData();
    }
    return await this.api.get(API_CONFIG.ENDPOINTS.USER_PROFILE);
  }

  // KPI Readings - Energy Loss
  async getForEnergyLoss(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getForEnergyLoss(params);
    }
    
    // Try primary endpoint; if missing on backend (404), fall back to aggregated EYA
    try {
      return await this.postCached(
        API_CONFIG.ENDPOINTS.GET_FOR_ENERGY_LOSS,
        params,
        API_CONFIG.CACHE_TTL.BRIEF
      );
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        // Normalize times to match Angular endpoint expectations
        const normalize = (dtStr, isStart) => {
          if (!dtStr) return dtStr;
          const dateOnly = dtStr.split(' ')[0];
          return `${dateOnly} ${isStart ? '00:00:00' : '23:55:00'}`;
        };
        const normalizedParams = {
          ...params,
          start_date: normalize(params.start_date, true),
          end_date: normalize(params.end_date, false),
        };
        return await this.postCached(
          API_CONFIG.ENDPOINTS.GET_AGGREGATED_EYA,
          normalizedParams,
          API_CONFIG.CACHE_TTL.BRIEF
        );
      }
      throw error;
    }
  }

  // KPI Readings - Graph Data
  async getForGraph(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getForGraph(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_FOR_GRAPH, 
      params, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  // KPI Readings - Aggregated Data
  async getAggregatedKpi(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getAggregatedKpi(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_AGGREGATED_KPI, 
      params, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  // KPI Readings - Aggregated EYA
  async getAggregatedEya(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getAggregatedEya(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_AGGREGATED_EYA, 
      params, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  // KPI Readings by Tags
  async getKpiReadingsByTags(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getKpiReadingsByTags(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.KPI_READINGS_BY_TAGS, 
      params, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  // PVSyst Data
  async getPvSystData(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getPvSystData(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_PVSYST_DATA, 
      params, 
      API_CONFIG.CACHE_TTL.LONG
    );
  }

  async getPvSystDataForEL(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getPvSystDataForEL(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.GET_PVSYST_DATA_FOR_EL, 
      params, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  // OMP Data by Tags
  async getOmpDataByTags(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getOmpDataByTags(params);
    }
    const requestBody = {
      ...params,
      tags_list: params.tags_list || ['DP_ACTIVE_POWER', 'WS_LIVE_POA_POWER_DENSITY']
    };
    
    return await this.postCached(
      API_CONFIG.ENDPOINTS.OMP_DATA_BY_TAGS, 
      requestBody, 
      API_CONFIG.CACHE_TTL.BRIEF
    );
  }

  // Power Forecast - Intra Day
  async getIntraDayForecast(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getIntraDayForecast(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.INTRA_DAY_FORECAST, 
      params, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  // Power Forecast - Inter Day
  async getInterDayForecast(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getInterDayForecast(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.INTER_DAY_FORECAST, 
      params, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  // Power Forecast - Week Ahead
  async getWeekAheadForecast(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getWeekAheadForecast(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.WEEK_AHEAD_FORECAST, 
      params, 
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  // Power Forecast - Month Ahead
  async getMonthAheadForecast(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getMonthAheadForecast(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.MONTH_AHEAD_FORECAST, 
      params, 
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  // Power Forecast - Historical
  async getHistoricalForecast(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getHistoricalForecast(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.HISTORICAL_FORECAST, 
      params, 
      API_CONFIG.CACHE_TTL.MEDIUM
    );
  }

  // Asset Data
  async fetchApoAssetData(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.fetchApoAssetData(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_DATA, 
      params, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async fetchApoAssetDataForAssetTable(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.fetchApoAssetDataForAssetTable(params);
    }
    // API requires asset_type and tag_list, provide defaults if not specified
    const requestParams = {
      ...params,
      plant_id: params?.plant_id || API_CONFIG.PLANT_ID,
      asset_type: params?.asset_type || 'INVERTER',
      tag_list: params?.tag_list || ['make', 'model', 'ac_rated_capacity', 'jhi_type']
    };
    
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_DATA_FOR_TABLE, 
      requestParams, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  async fetchApoAssetAttrData(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.fetchApoAssetAttrData(params);
    }
    return await this.postCached(
      API_CONFIG.ENDPOINTS.FETCH_APO_ASSET_ATTR_DATA, 
      params, 
      API_CONFIG.CACHE_TTL.SHORT
    );
  }

  // User Management
  async getUsers(params = {}) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getUsers(params);
    }
    const queryParams = new URLSearchParams({
      skip: params.skip || 0,
      limit: params.limit || 100,
      ...params
    });
    return await this.api.get(`${API_CONFIG.ENDPOINTS.USERS}?${queryParams}`);
  }

  async getUserPlantAccess(params = {}) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getUserPlantAccess(params);
    }
    const queryParams = new URLSearchParams({
      skip: params.skip || 0,
      limit: params.limit || 100,
      ...params
    });
    return await this.api.get(`${API_CONFIG.ENDPOINTS.APO_USER_PLANT_ACCESS}?${queryParams}`);
  }

  async updateUser(userId, userData) {
    if (USE_MOCK_DATA) {
      return await mockApiService.updateUser(userId, userData);
    }
    return await this.api.put(`${API_CONFIG.ENDPOINTS.USERS}/${userId}/`, userData);
  }

  // Utility methods for data processing (matching Angular functionality)
  setCardData(config, response) {
    if (config && response && response.cumulative) {
      const paramKeyArray = Object.keys(config);
      if (paramKeyArray && paramKeyArray.length) {
        paramKeyArray.forEach(param => {
          let paramCodeValue = '-';
          if (response.cumulative[param] !== null && response.cumulative[param] !== undefined) {
            paramCodeValue = Number(response.cumulative[param]).toFixed(2);
          }
          config[param].value = paramCodeValue;
        });
      }
    }
  }

  clearVariableParamValue(config) {
    if (config) {
      const paramKeyArray = Object.keys(config);
      if (paramKeyArray && paramKeyArray.length) {
        paramKeyArray.forEach(param => {
          config[param].value = '-';
        });
      }
    }
  }

  // Chart configuration helpers (matching Angular power forecast service)
  getPlotOptionsForLineColumnChart() {
    return {
      column: {
        borderWidth: 0
      },
      line: {
        marker: {
          enabled: false
        }
      },
      series: {
        events: {
          legendItemClick() {
            const chart = this.chart;
            if (this.index === 0 && chart.series[0].name === 'Hide All') {
              chart.series[0].update({ name: 'Show All' });
              chart.series.forEach(series => {
                series.setVisible(false, false);
              });
            } else if (this.index === 0 && chart.series[0].name === 'Show All') {
              chart.series[0].update({ name: 'Hide All' });
              chart.series.forEach(series => {
                series.setVisible(true, false);
              });
            } else {
              chart.series[0].setVisible(false, false);
            }
          }
        }
      }
    };
  }

  getXAxisType() {
    return {
      type: 'datetime',
    };
  }

  // Export functionality (matching Angular service)
  exportTable(type, tableData, columnsToDisplay, presentColumns, tableRefEle, fileName) {
    switch (type) {
      case 'csv':
        this.downloadCsv(tableData, columnsToDisplay, presentColumns, fileName);
        break;
      case 'xlsx':
        this.downloadExcel(tableRefEle, fileName);
        break;
    }
  }

  downloadCsv(tableData, columnsToDisplay, presentColumns, fileName) {
    const headersInput = this.getCSVHeaders(columnsToDisplay, presentColumns);
    const csvOptions = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalseparator: '.',
      showLabels: true,
      useBom: true,
      noDownload: false,
      headers: headersInput
    };
    
    let copyTableData = [];
    if (tableData && tableData.length) {
      copyTableData = JSON.parse(JSON.stringify(tableData));
      copyTableData.forEach(obj => {
        Object.keys(obj || []).forEach(element => {
          if (!columnsToDisplay.includes(element)) {
            delete obj[element];
          }
        });
      });
    }
    
    // Create and download CSV
    const csvContent = this.convertToCSV(copyTableData, csvOptions);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  downloadExcel(elementRef, fileName) {
    // Note: This would require a library like xlsx to be installed
    // For now, we'll implement CSV export as a fallback
    console.warn('Excel export requires xlsx library. Falling back to CSV.');
    this.downloadCsv([], [], [], fileName);
  }

  getCSVHeaders(columnsToDisplay, presentColumns) {
    const headers = [];
    if (presentColumns && presentColumns.length && columnsToDisplay && columnsToDisplay.length) {
      presentColumns.map(column => {
        if (columnsToDisplay.includes(column.code)) {
          headers.push(column.name);
        }
      });
    }
    return headers;
  }

  convertToCSV(data, options) {
    if (!data || !data.length) return '';
    
    const headers = options.headers || Object.keys(data[0]);
    const csvRows = [];
    
    // Add headers
    csvRows.push(headers.join(options.fieldSeparator));
    
    // Add data rows
    for (const row of data) {
      const values = headers.map(header => {
        const value = row[header] || '';
        const escaped = String(value).replace(/"/g, '""');
        return `"${escaped}"`;
      });
      csvRows.push(values.join(options.fieldSeparator));
    }
    
    return csvRows.join('\n');
  }

  // Plant Availability endpoints
  async getInverterAvailability(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getInverterAvailability(params);
    }
    return await this.post(API_CONFIG.ENDPOINTS.GET_INVERTER_AVAILABILITY || '/api/inverter-availability', params);
  }

  async getStringCombinerBoxAvailability(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getStringCombinerBoxAvailability(params);
    }
    return await this.post(API_CONFIG.ENDPOINTS.GET_STRING_COMBINER_BOX_AVAILABILITY || '/api/string-combiner-box-availability', params);
  }

  async getTrackerAvailability(params) {
    if (USE_MOCK_DATA) {
      return await mockApiService.getTrackerAvailability(params);
    }
    return await this.post(API_CONFIG.ENDPOINTS.GET_TRACKER_AVAILABILITY || '/api/tracker-availability', params);
  }
}

const apiService = new ApiService();
export default apiService;